// Подписи и форматы для экранов. Хранение — каноническое (сумма «120000.00»,
// дата ISO), показ — по-русски; обратно в API уходит то, что ввёл человек,
// а приводит к канону сервер.
import type {
  DocumentSummary,
  FieldSpec,
  FieldType,
  TemplateRequest,
  ValueSource,
} from '@/api/client';

import { parseItems } from './items';

const DATE = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });
const DATE_SHORT = new Intl.DateTimeFormat('ru-RU', { day: 'numeric', month: 'short' });
const TIME = new Intl.DateTimeFormat('ru-RU', { hour: '2-digit', minute: '2-digit' });
const NUMBER = new Intl.NumberFormat('ru-RU');

// «1 284»: разряды по-русски.
export function formatNumber(value: number): string {
  return NUMBER.format(value);
}

export function formatDate(iso: string | null | undefined): string {
  if (!iso) return '—';
  return DATE.format(new Date(iso));
}

// «сегодня, 14:05», «вчера», «12 сент.» — для списков.
export function formatRelative(iso: string, now: Date = new Date()): string {
  const date = new Date(iso);
  const day = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((day(now) - day(date)) / 86_400_000);
  if (diffDays === 0) return `сегодня, ${TIME.format(date)}`;
  if (diffDays === 1) return 'вчера';
  if (date.getFullYear() === now.getFullYear()) return DATE_SHORT.format(date);
  return DATE.format(date);
}

// «Счёт на оплату № 17»: документы одного вида в архиве различаются номером.
export function documentName(title: string, number: string | null | undefined): string {
  return number && !title.includes(number) ? `${title} № ${number}` : title;
}

export function formatDateTime(iso: string): string {
  const date = new Date(iso);
  return `${DATE_SHORT.format(date)}, ${TIME.format(date)}`;
}

export function formatMoney(raw: string): string {
  const amount = Number(raw);
  if (!Number.isFinite(amount)) return raw;
  // Пробел между разрядами — обычный, как в документе: неразрывный из Intl
  // выглядел бы так же, но ломал бы сравнение «человек поменял значение».
  return amount
    .toLocaleString('ru-RU', {
      minimumFractionDigits: Number.isInteger(amount) ? 0 : 2,
      maximumFractionDigits: 2,
    })
    .replace(/\s/g, ' ');
}

// Позиции строками для экрана проверки: «1. Разработка сайта — 2 усл. × 1 500 ₽».
export function itemsSummary(raw: string): string {
  return parseItems(raw)
    .map((row, index) => {
      const unit = row.unit ? ` ${row.unit}` : '';
      return `${index + 1}. ${row.name} — ${row.quantity || '1'}${unit} × ${formatMoney(row.price)} ₽`;
    })
    .join('\n');
}

// Значение из API → строка для показа: сумма и дата по-русски, позиции — списком.
export function displayValue(type: FieldType, raw: string): string {
  if (type === 'items') return itemsSummary(raw);
  if (type === 'date') {
    const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
    return match ? `${match[3]}.${match[2]}.${match[1]}` : raw;
  }
  return toInputValue(type, raw);
}

// Значение из API → строка для поля ввода (дата остаётся ISO для <input type="date">).
export function toInputValue(type: FieldType, raw: string): string {
  if (type === 'money') return formatMoney(raw);
  return raw;
}

export interface InputKind {
  type: 'text' | 'email' | 'tel' | 'date';
  inputMode?: 'text' | 'numeric' | 'decimal' | 'email' | 'tel';
  multiline?: boolean;
  suffix?: string;
}

export function inputKind(type: FieldType): InputKind {
  switch (type) {
    // Адрес в одну строку на телефоне не перечитать: видно только начало.
    case 'address':
    case 'multiline':
    case 'items':
      return { type: 'text', multiline: true };
    case 'email':
      return { type: 'email', inputMode: 'email' };
    case 'phone':
      return { type: 'tel', inputMode: 'tel' };
    case 'date':
      return { type: 'date' };
    case 'money':
      return { type: 'text', inputMode: 'decimal', suffix: '₽' };
    case 'integer':
    case 'inn':
    case 'kpp':
    case 'ogrn':
    case 'bic':
    case 'account':
      return { type: 'text', inputMode: 'numeric' };
    default:
      return { type: 'text' };
  }
}

export const SOURCE_LABEL: Record<ValueSource, string> = {
  manual: 'Вручную',
  profile: 'Из профиля',
  counterparty: 'Из карточки',
  ocr: 'С фото',
  agent: 'От помощника',
  default: 'По умолчанию',
  file: 'Из файла',
};

// Группы полей шаблона → разделы формы. Сначала стороны — от кого и кому, —
// потом условия: так документ заполняют владельцы, стороны определяют, какие
// условия вообще писать.
export const GROUP_ORDER = ['Продавец', 'Клиент', 'Предмет'] as const;
export const GROUP_TITLE: Record<string, string> = {
  Клиент: 'Клиент',
  Предмет: 'Условия',
  Продавец: 'Ваша организация',
};

export function groupFields(fields: FieldSpec[]): Array<[string, FieldSpec[]]> {
  const groups = new Map<string, FieldSpec[]>();
  for (const field of fields) {
    const list = groups.get(field.group) ?? [];
    list.push(field);
    groups.set(field.group, list);
  }
  const rank = (group: string) => {
    const index = (GROUP_ORDER as readonly string[]).indexOf(group);
    return index === -1 ? GROUP_ORDER.length : index;
  };
  return [...groups.entries()].sort(([a], [b]) => rank(a) - rank(b));
}

// Вид документа → подписи: коротко для выбора типа, во множественном числе для
// фильтров и графиков, полностью — для поиска по каталогу («договоры», «счета»).
export type TemplateKind = TemplateRequest['kind'];

export interface KindStyle {
  short: string;
  plural: string;
  section: string;
}

export const KIND_STYLE: Record<TemplateKind, KindStyle> = {
  invoice: { short: 'Счёт', plural: 'Счета', section: 'Счета' },
  offer: { short: 'КП', plural: 'КП', section: 'Коммерческие предложения' },
  contract: { short: 'Договор', plural: 'Договоры', section: 'Договоры' },
  act: { short: 'Акт', plural: 'Акты', section: 'Акты' },
  other: { short: 'Другое', plural: 'Другие', section: 'Другие документы' },
};

export const KIND_ORDER: TemplateKind[] = ['invoice', 'offer', 'contract', 'act', 'other'];
// Из каких документов делается акт выполненных работ (core.usecases.documents.acts).
export const ACT_SOURCES: readonly string[] = ['invoice', 'contract', 'offer'];

export function asKind(kind: string | undefined): TemplateKind {
  return (KIND_ORDER as string[]).includes(kind ?? '') ? (kind as TemplateKind) : 'other';
}

export function kindStyle(kind: string | undefined): KindStyle {
  return KIND_STYLE[asKind(kind)];
}

export interface DocumentState {
  label: string;
  tone: 'draft' | 'ready' | 'sent' | 'failed';
}

// Состояние документа одной строкой: черновик → готов → отправлен → в чате.
export function documentState(doc: Pick<DocumentSummary, 'status' | 'sent'>): DocumentState {
  if (doc.sent) {
    const format = doc.sent.format ? ` ${doc.sent.format.toUpperCase()}` : '';
    if (doc.sent.delivery === 'delivered') return { label: `В чате${format}`, tone: 'sent' };
    if (doc.sent.delivery === 'failed') return { label: 'Не доставлен', tone: 'failed' };
    return { label: `Отправляется${format}`, tone: 'sent' };
  }
  if (doc.status === 'ready') return { label: 'Готов', tone: 'ready' };
  return { label: 'Черновик', tone: 'draft' };
}

export const FACT_LABEL: Record<string, string> = {
  created: 'Создан',
  ready: 'Все поля заполнены',
  rendered: 'Файл собран',
  sent: 'Отправлен в чат',
  delivered: 'Доставлен в чат',
  delivery_failed: 'Не доставлен',
  rejected: 'Значение не прошло проверку',
};

export function pluralize(count: number, one: string, few: string, many: string): string {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
  return many;
}

// Буквы для аватара организации: без организационно-правовой формы.
export function initials(name: string): string {
  const words = name
    .replace(/[«»"']/g, '')
    .split(/\s+/)
    .filter((word) => word && !/^(ООО|АО|ПАО|ЗАО|ИП|ОАО)$/i.test(word));
  return (
    words
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase())
      .join('') || '?'
  );
}
